import type * as React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { parseCapitalizeString } from '@/lib/utils';

import { pickAutoLinkCandidate } from '@heidi/patients';
import type { PatientProfileSearchResult } from '@heidi/patients/contracts';
import { useLocalPatientSearch } from './useLocalPatientSearch';
import { useSearchPatientProfiles } from './useSearchPatientProfiles';

/** Max patient rows fetched per Heidi-profile search; the dropdown scrolls beyond this. */
export const PATIENT_SEARCH_RESULT_LIMIT = 5;

interface UseFindOrLinkSearchArgs {
  /**
   * The session-persisted patient name (from `useFindOrLinkSessionState`).
   * Seeds the input value while the user hasn't touched it; ignored once
   * the input is dirty until an explicit `resetQuery` clears the dirty
   * flag (called by the controller on link/unlink transitions).
   */
  committedName: string;
  enabled: boolean;
  /**
   * Scopes the BE search to the patient-profile owner resolved from this
   * session. Required for the assistant-acting-for-clinician case so the
   * assistant sees the clinician's patients, not their own (empty) list.
   * Passed through to `useSearchPatientProfiles`.
   */
  sessionId?: string;
  /**
   * Funnel-tracking context threaded down from the controller. The search
   * hook just passes this through to `useSearchPatientProfiles` so each
   * BE-confirmed search can fire `patient_searched` carrying the same
   * `search_id` the rest of the funnel uses. Optional — without it, no
   * patient_searched event fires.
   */
  tracking?: {
    sessionId: string | undefined;
    searchIdRef: React.MutableRefObject<string | undefined>;
    featureFlagVariant: 'treated' | 'control' | 'unknown';
  };
  /**
   * Page size for POST /patient-profiles:search. Defaults to
   * PATIENT_SEARCH_RESULT_LIMIT.
   */
  limit?: number;
  /**
   * When false, skip the local GET /patient-profiles/ roster index.
   */
  localSearchEnabled?: boolean;
}

interface FindOrLinkSearch {
  query: string;
  setQuery: (next: string) => void;
  /**
   * Externally-callable reset — used by the parent on link/unlink
   * transitions to snap the input back to `committedName` and clear the
   * dirty flag.
   */
  resetQuery: () => void;

  /** IME composition handlers — pass through to the input. */
  onCompositionStart: () => void;
  onCompositionEnd: (e: React.CompositionEvent<HTMLInputElement>) => void;

  /**
   * Merged result list: instant local-roster matches first (flag-gated),
   * then BE-only rows (kept across debounces by TanStack Query). See the
   * module docstring's "Search results" section.
   */
  results: PatientProfileSearchResult[];
  isFetching: boolean;
  /**
   * True after this query has settled locally: debounce has caught up and
   * there is no in-flight request. Independent of fetch outcome. Callers
   * that must not treat a failed fetch as an empty roster also check
   * `isError`.
   */
  beIsFresh: boolean;
  /**
   * True when the last BE search for the current query failed. Callers
   * render a search-unavailable notice instead of the empty state.
   */
  isError: boolean;
  /**
   * Pre-highlighted candidate when the auto-link rule applies. Snapshot
   * is preserved across debounces so the "Press Enter to link" hint
   * doesn't strobe between keystrokes.
   */
  autoLinkCandidate: PatientProfileSearchResult | null;
}

/**
 * Owns the typed query, IME composition, capitalize-on-type, and the
 * BE-driven search result list. Pure derived state — no mutations,
 * no session reads.
 *
 * # Input-value state machine
 *
 * The displayed input value is governed by ONE of three states:
 *
 *   ┌─────────────┐  user types       ┌─────────────┐
 *   │  COMMITTED  │ ────────────────▶ │    DIRTY    │
 *   │ (clean,     │                   │ (typed,     │
 *   │  reflects   │ ◀──────────────── │  diverged   │
 *   │  session)   │  resetQuery()     │  from       │
 *   └─────────────┘  on link/unlink   │  session)   │
 *         ▲                           └─────────────┘
 *         │                                  │
 *         │ session refetch                  │ session refetch
 *         │ (re-seeds queryRaw)              │ (IGNORED — local wins)
 *         │                                  │
 *         └──────────────────────────────────┘
 *
 * - COMMITTED: `hasUserTypedRef = false`. Background session refetches
 *   re-seed `queryRaw` from `committedName`. This is the steady state
 *   on mount and after a link/unlink transition.
 * - DIRTY: `hasUserTypedRef = true`. The user has touched the input;
 *   the typed value is authoritative. Session refetches are dropped on
 *   the floor — otherwise transcript-driven patient extraction or
 *   optimistic-update churn would clobber half-typed names.
 * - resetQuery() is the explicit transition back to COMMITTED. Called
 *   by the controller on every link/unlink so a freshly-unlinked
 *   session re-seeds from its (now-empty) `session.patient.name`.
 *
 * # Search results
 *
 * Two sources feed the displayed list:
 *
 * 1. **Local-first layer** (`useLocalPatientSearch`, flag-gated) —
 *    matches the cached roster synchronously against the LIVE query, so
 *    results appear on the first keystroke (below the BE index's 2-char
 *    gram minimum, and without the debounce + network wait).
 * 2. **BE Atlas Search** (`useSearchPatientProfiles`) — the canonical
 *    source, debounced, from 2 chars.
 *
 * They are MERGED, never swapped: local rows first, BE-only rows
 * appended, deduped by id. History matters here — an earlier version
 * ran a client-side filter that GATED the list during the debounce
 * window; its matching was stricter than the BE's, so one extra
 * keystroke could empty the dropdown for ~150 ms before BE confirmed,
 * which read as flicker, and it was removed. The merge is safe where
 * that wasn't because neither source gates the other: local results are
 * a pure synchronous function of the live query (they can never lag a
 * keystroke), and a BE response only ADDS rows it found that local
 * didn't. The matcher deliberately mirrors BE semantics (see
 * `local-patient-matcher.ts`) so the two sets rarely disagree; when
 * they do, local errs looser — an extra plausible row, never a
 * vanishing one. `keepPreviousData` still bridges BE gaps between
 * debounces, and the auto-link rule stays keyed to BE results only
 * (linking is a write; locally-matched-but-unconfirmed rows never arm
 * it).
 */
export function useFindOrLinkSearch({
  committedName,
  enabled,
  sessionId,
  tracking,
  limit = PATIENT_SEARCH_RESULT_LIMIT,
  localSearchEnabled = true,
}: UseFindOrLinkSearchArgs): FindOrLinkSearch {
  // Track IME composition so we don't auto-capitalize romaji-to-kana
  // partial input.
  const isComposingRef = useRef(false);
  // COMMITTED ↔ DIRTY toggle for the input-value state machine — see
  // module-level docstring above.
  const hasUserTypedRef = useRef(false);

  const onCompositionStart = useCallback(() => {
    isComposingRef.current = true;
  }, []);
  const onCompositionEnd = useCallback((e: React.CompositionEvent<HTMLInputElement>) => {
    isComposingRef.current = false;
    hasUserTypedRef.current = true;
    setQueryRaw(parseCapitalizeString(e.currentTarget.value));
  }, []);

  const [queryRaw, setQueryRaw] = useState(committedName);
  // Sync from the session-derived value, but only while the input is
  // "clean" (user hasn't edited locally). Once dirty, we hold the typed
  // value until an explicit `resetQuery` (link/unlink).
  useEffect(() => {
    if (hasUserTypedRef.current) return;
    setQueryRaw(committedName);
  }, [committedName]);

  const setQuery = useCallback((next: string) => {
    hasUserTypedRef.current = true;
    setQueryRaw(isComposingRef.current ? next : parseCapitalizeString(next));
  }, []);
  // Snap back to the latest persisted value AND clear the dirty flag.
  // Called by the controller on link/unlink transitions so a subsequent
  // session refetch is allowed to seed again.
  const committedNameRef = useRef(committedName);
  committedNameRef.current = committedName;
  const resetQuery = useCallback(() => {
    hasUserTypedRef.current = false;
    setQueryRaw(committedNameRef.current);
  }, []);
  const query = queryRaw;

  const {
    results: rawBeResults,
    isFetching,
    isError,
    debouncedQuery,
  } = useSearchPatientProfiles({
    query,
    enabled,
    sessionId,
    tracking,
    limit,
  });
  // Local-first layer — instant, flag-gated, empty when inactive so the
  // merge below degrades to exactly the BE-only behaviour.
  const { localResults } = useLocalPatientSearch({
    query,
    enabled: enabled && localSearchEnabled,
    sessionId,
  });

  const trimmedQuery = query.trim();
  // Settled-for-this-query only. Callers that must not treat an error as
  // an empty roster (speaker panel) gate on `isError` themselves. Folding
  // it in here would blank the header combobox empty-state and disable
  // Enter-to-create, which still key off beIsFresh alone.
  const beIsFresh =
    !isFetching && trimmedQuery.length > 0 && debouncedQuery === trimmedQuery;

  // Hide kept-from-previous-query rows that don't match what the user
  // is currently typing. TanStack Query's `keepPreviousData` lets us
  // keep rendering the previous result list while a new debounce is in
  // flight — that prevents flicker WITHIN a search ("Da" → "Dav") but
  // also leaves stale rows visible when the user changes context
  // ("Test" → "Phil"). This filter mirrors the BE's tokenised substring
  // match so any row that wouldn't survive a fresh fetch is dropped
  // immediately on the next keystroke.
  //
  // The filter keys off `debouncedQuery` (not the live `query`) so the
  // filter mirrors what the BE was actually asked for — otherwise rows
  // the BE just confirmed get stripped client-side during the window
  // where `query !== debouncedQuery`.
  const filterTokens = useMemo(() => {
    const trimmed = debouncedQuery.trim();
    if (!trimmed) return [];
    return trimmed.toLowerCase().split(/\s+/).filter(Boolean);
  }, [debouncedQuery]);
  const filteredBeResults = useMemo<PatientProfileSearchResult[]>(() => {
    if (filterTokens.length === 0) return [];
    return rawBeResults.filter((row) => {
      const haystack = [row.first_name, row.last_name, row.demographic_string]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return filterTokens.every((t) => haystack.includes(t));
    });
  }, [rawBeResults, filterTokens]);

  // Hold onto the most recent BE-fresh auto-link candidate while a new
  // debounce is in flight so the pre-highlight doesn't strobe between
  // keystrokes — only updates once BE confirms a new view. Use the raw
  // (unfiltered) BE results here: the filter above hides stale rows
  // for display, but the auto-link rule cares about the canonical view.
  const liveAutoLink = useMemo(
    () => (beIsFresh ? pickAutoLinkCandidate(query, rawBeResults) : null),
    [beIsFresh, query, rawBeResults]
  );
  // Snapshot is keyed on the debounced query so a new keystroke
  // invalidates the previous query's pre-highlight in the same render —
  // otherwise typing "John Doe" → backspacing to "John Do" briefly
  // pre-highlights the *old* match for the duration of the debounce.
  const lastFreshRef = useRef<PatientProfileSearchResult | null>(null);
  const lastFreshQueryRef = useRef<string>('');
  if (beIsFresh) {
    lastFreshRef.current = liveAutoLink;
    lastFreshQueryRef.current = debouncedQuery;
  } else if (debouncedQuery !== lastFreshQueryRef.current) {
    lastFreshRef.current = null;
  }
  if (trimmedQuery.length === 0 && lastFreshRef.current !== null) {
    lastFreshRef.current = null;
  }
  const autoLinkCandidate = beIsFresh ? liveAutoLink : lastFreshRef.current;

  // Merge, never swap: local rows lead (they track the live query with
  // zero lag), BE-only rows are appended. A BE response can only ADD
  // rows to what local already shows — removing a visible row on a
  // keystroke is the historical flicker this design exists to prevent
  // (see the module docstring). With local search inactive,
  // `localResults` is [] and this is byte-identical to the old
  // BE-only list.
  //
  // The BE-confirmed auto-link candidate is PINNED to the top: local
  // fills up to 10 rows on its own, so without the pin, 10 local
  // homonyms could squeeze the one row Enter is armed to link out of
  // the list entirely — the combobox would highlight an id that isn't
  // rendered and Enter would silently do nothing. Pinning also keeps
  // the "press Enter to link" row visually first, which is what that
  // hint implies.
  const results = useMemo<PatientProfileSearchResult[]>(() => {
    if (localResults.length === 0) return filteredBeResults;
    const localIds = new Set(localResults.map((row) => row.id));
    // Local rows keep their POSITION (stable ordering is the
    // anti-flicker guarantee) but a row present in both sources
    // renders the BE COPY — the local one comes from a cache up to 1h
    // old (stale demographic_string / session_count after an edit
    // elsewhere).
    const beById = new Map(filteredBeResults.map((row) => [row.id, row]));
    const merged = [
      ...localResults.map((row) => beById.get(row.id) ?? row),
      ...filteredBeResults.filter((row) => !localIds.has(row.id)),
    ];
    if (autoLinkCandidate) {
      return [
        autoLinkCandidate,
        ...merged.filter((row) => row.id !== autoLinkCandidate.id),
      ].slice(0, 10);
    }
    return merged.slice(0, 10);
  }, [localResults, filteredBeResults, autoLinkCandidate]);

  return {
    query,
    setQuery,
    resetQuery,
    onCompositionStart,
    onCompositionEnd,
    results,
    isFetching,
    beIsFresh,
    isError,
    autoLinkCandidate,
  };
}
